import { Delegate } from './delegate.js';

const MOBILE_LAYOUT_QUERY = '(max-width: 767px)';
const SAVE_WITH_KITS_MOBILE_BODY_NAV_PADDING = 16;
const SAVE_WITH_KITS_DESKTOP_BELOW_CHROME_PX = 20;
const SAVE_WITH_KITS_DESKTOP_ABOVE_BUY_BUTTON_PX = 13;
const SAVE_WITH_KITS_DESKTOP_ABOVE_ATC_PX = 16;
const SAVE_WITH_KITS_FEEDBACK_GAP_PX = 12;
const SAVE_WITH_KITS_MOBILE_NAV_FALLBACK_OFFSET_PX = 100;
const MOBILE_PANEL_EXPANDED = true;
const MOBILE_PEEK_ACTIVE = true;
const OVERLAY_BODY_CLASSES = [
  'search-open',
  'has-dropdown-menu',
  'modal-show',
  'modal-showing',
  'hr-search-disable-scroll',
];
const HELLO_RETAIL_RESULTS_SELECTOR = '.hr-results';

class ProductSaveWithKits extends HTMLElement {
  #abortController = null;
  #delegate = null;
  #headerObserver = null;
  #headerMutationObserver = null;
  #buyButtonsObserver = null;
  #kitsPanelObserver = null;
  #overlayObserver = null;
  #overlaySyncRaf = null;
  #layoutScrollRaf = null;
  #mobileLayoutMq = null;
  #panel = null;
  #panelOriginalNextSibling = null;
  #panelOriginalParent = null;
  #prefersReducedMotion = false;
  #topSyncPending = false;
  #topSyncPostRaf = null;
  #topSyncRaf = null;
  #userDismissed = false;
  #mobileBarInsetAnimated = false;
  #saveLabelTemplate = '';
  #valueLabel = '';
  #currentPdpPriceCents = 0;
  #revealDelayMs = 0;
  #revealTimeoutId = null;
  #sectionId = '';

  connectedCallback() {
    if (this.#abortController) return;

    this.#abortController = new AbortController();
    this.#mobileLayoutMq = window.matchMedia?.(MOBILE_LAYOUT_QUERY); // matchMedia: legacy theme has no responsive utility module.
    this.#prefersReducedMotion = Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);

    this.#initKitsLabelState();
    this.#bindGlobalEvents();
  }

  disconnectedCallback() {
    if (!this.#abortController) return;
    this.#destroy();
  }

  #mountPanelToBody() {
    if (!this.#panel) return;
    if (this.#panel.parentNode === document.body) return;

    this.#panelOriginalParent = this.#panel.parentNode;
    this.#panelOriginalNextSibling = this.#panel.nextSibling;
    document.body.appendChild(this.#panel);
  }

  #restorePanelMount() {
    if (!this.#panelOriginalParent || !this.#panel) return;

    if (this.#panel.parentNode === document.body) {
      if (
        this.#panelOriginalNextSibling &&
        this.#panelOriginalNextSibling.parentNode === this.#panelOriginalParent
      ) {
        this.#panelOriginalParent.insertBefore(this.#panel, this.#panelOriginalNextSibling);
      } else {
        this.#panelOriginalParent.appendChild(this.#panel);
      }
    }

    this.#panelOriginalParent = null;
    this.#panelOriginalNextSibling = null;
  }

  #bindDelegatedEvents() {
    this.#delegate = new Delegate(this.#panel);
    this.#delegate.on('click', '[data-action="close-kits-popup"]', this.#onCloseClick);
    this.#delegate.on('click', '[data-action="toggle-kits-panel"]', this.#onMobileTriggerClick);
    this.#delegate.on('keypress', '[data-action="close-kits-popup"], [data-action="toggle-kits-panel"]', this.#onActionKeypress);
  }

  #bindGlobalEvents() {
    const { signal } = this.#abortController;

    document.addEventListener('keydown', this.#onKeydown, { signal }); // delegate-exempt: global Escape key handling.
    document.addEventListener('variant:changed', this.#onVariantChanged, { signal }); // delegate-exempt: sync kit labels when PDP variant changes.
    window.addEventListener('resize', this.#onResize, { passive: true, signal }); // delegate-exempt: viewport resize has no DOM target.
    if (document.readyState === 'complete') {
      this.#onLoad();
    } else {
      window.addEventListener('load', this.#onLoad, { once: true, signal }); // delegate-exempt: reveal after window load.
    }
    window.addEventListener('scroll', this.#onScrollLayoutSync, { passive: true, signal }); // delegate-exempt: viewport scroll drives popup position.
    window.addEventListener('wheel', this.#resyncAfterHeader, { passive: true, once: true, signal }); // delegate-exempt: one-time header height sync after interaction.
    window.addEventListener('touchstart', this.#resyncAfterHeader, { passive: true, once: true, signal }); // delegate-exempt: one-time header height sync after interaction.
    document.addEventListener('drawer:handleAfterShow', this.#syncBehindOverlay, { signal }); // delegate-exempt: hide kits while drawers are open.
    document.addEventListener('drawer:handleAfterHide', this.#syncBehindOverlay, { signal }); // delegate-exempt: restore kits after drawers close.
    document.addEventListener('sticky-atc:change', this.#onStickyAtcChange, { signal }); // delegate-exempt: sync chat offset when sticky ATC toggles.

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.#onVisualViewportResize, { passive: true, signal }); // delegate-exempt: visual viewport resize has no DOM target.
    }

    if (this.#mobileLayoutMq?.addEventListener) {
      this.#mobileLayoutMq.addEventListener('change', this.#onMobileLayoutMqChange, { signal });
    } else if (this.#mobileLayoutMq?.addListener) {
      this.#mobileLayoutMq.addListener(this.#onMobileLayoutMqChange);
    }
  }

  /**
   * Clones the inert template into the live DOM, then wires panel listeners.
   * @returns {boolean}
   */
  #hydratePanel() {
    if (this.#panel) return true;

    const template = this.querySelector(':scope > template[data-save-with-kits-template]');
    if (template) {
      this.append(template.content.cloneNode(true));
      template.remove();
    }

    this.#panel = this.querySelector('[data-save-with-kits-panel]');
    if (!this.#panel) return false;

    this.#mountPanelToBody();

    const { signal } = this.#abortController;

    this.#bindDelegatedEvents();
    this.#panel.addEventListener('transitionrun', this.#onPanelTransition, { passive: true, signal }); // delegate-exempt: keep CSS vars synced while mobile panel animates.
    this.#panel.addEventListener('transitionend', this.#onPanelTransition, { passive: true, signal }); // delegate-exempt: final sync after open/close transition.
    this.#observeKitsPanel();
    this.#observeHeaderChrome();
    this.#observeBuyButtonsAnchor();
    this.#observeOverlayState();
    this.#syncSaveWithKitsTop();
    this.#syncSaveWithKitsMobileBodyMaxHeight();
    this.#scheduleSyncSaveWithKitsTop();
    return true;
  }

  #observeKitsPanel() {
    if (typeof ResizeObserver === 'undefined') return;

    this.#kitsPanelObserver = new ResizeObserver(() => {
      this.#syncMobileBarInset();
      this.#syncSaveWithKitsMobileBodyMaxHeight();
      this.#scheduleSyncSaveWithKitsTop();
    });
    this.#kitsPanelObserver.observe(this.#panel);
  }

  #observeHeaderChrome() {
    if (typeof ResizeObserver === 'undefined') return;

    const observed = new WeakSet();

    this.#headerObserver = new ResizeObserver(() => {
      this.#syncLayoutAfterHeaderChange();
    });

    const observe = (element) => {
      if (!element || observed.has(element)) return;

      observed.add(element);
      this.#headerObserver.observe(element);
    };

    const group = document.querySelector('.shopify-section-group-header-group');

    observe(group);
    group?.querySelectorAll(':scope > .shopify-section').forEach(observe);

    document
      .querySelectorAll(
        '.announcement-bar-section, .announcement-bar, announcement-bar, .topbar-section, #theme-header, header[is="sticky-header"], header[is="basic-header"], .header__top, .header__bottom'
      )
      .forEach(observe);

    if (group && typeof MutationObserver !== 'undefined') {
      this.#headerMutationObserver = new MutationObserver(() => {
        group.querySelectorAll(':scope > .shopify-section').forEach(observe);
        document.querySelectorAll('announcement-bar, .announcement-bar').forEach(observe);
        this.#syncLayoutAfterHeaderChange();
      });

      this.#headerMutationObserver.observe(group, {
        childList: true,
        subtree: true,
      });
    }
  }

  /**
   * Re-syncs popup layout when header / announcement bar chrome changes size.
   * Desktop: reposition popup below header chrome.
   * Mobile: only refresh viewport-dependent mobile metrics.
   * @returns {void}
   */
  #syncLayoutAfterHeaderChange() {
    if (this.#isMobileSaveWithKitsLayout()) {
      this.#syncSaveWithKitsMobileBodyMaxHeight();

      if (this.#shouldApplyMobileBarInset()) {
        this.#syncMobileBarInset({
          animate: this.#mobileBarInsetAnimated,
        });
      }

      return;
    }

    this.#syncSaveWithKitsTop();
  }

  /**
   * Observes the buy-buttons block so desktop popup top can track layout shifts.
   * @returns {void}
   */
  #observeBuyButtonsAnchor() {
    if (typeof ResizeObserver === 'undefined') return;

    const info = this.closest('.product__info-container');
    const anchors = [
      this.#getBuyButtonsAnchor(),
      info?.querySelector('.product__block--title'),
      info?.querySelector('.product__block--price'),
      info?.querySelector('.product__block--variant_picker'),
      info?.querySelector('.product__block--connected_products'),
    ].filter(Boolean);

    if (!anchors.length) return;

    this.#buyButtonsObserver = new ResizeObserver(() => {
      if (this.#isMobileSaveWithKitsLayout()) {
        this.#syncSaveWithKitsMobileBodyMaxHeight();
        return;
      }

      this.#syncLayoutAfterHeaderChange();
    });
    anchors.forEach((anchor) => this.#buyButtonsObserver.observe(anchor));
  }

  /**
   * Reads kit label templates and initial PDP price from data attributes.
   * @returns {void}
   */
  #initKitsLabelState() {
    this.#sectionId = this.dataset.sectionId || '';
    this.#saveLabelTemplate = this.dataset.kitsSaveLabelTemplate || 'Save [[amount]]';
    this.#valueLabel = this.dataset.kitsValueLabel || 'Prisvärt';
    this.#currentPdpPriceCents = Number(this.dataset.pdpPrice) || 0;
    const revealDelaySeconds = Number(this.dataset.revealDelay);
    this.#revealDelayMs = Math.max(0, Number.isFinite(revealDelaySeconds) ? revealDelaySeconds : 3) * 1000;
  }

  /**
   * Returns kit carousel slides that expose kit price data attributes.
   * @returns {HTMLElement[]}
   */
  #getKitSlides() {
    return this.#panel ? Array.from(this.#panel.querySelectorAll('.product__save-with-kits-slide[data-kit-price]')) : [];
  }

  /**
   * Formats a cents amount using theme or Shopify money helpers.
   * @param {number} cents - Price in cents.
   * @returns {string}
   */
  #formatMoney(cents) {
    const amount = Math.max(0, Math.round(Number(cents) || 0));
    const moneyFormat = FoxTheme?.settings?.moneyFormat;

    if (moneyFormat && typeof FoxTheme?.Currency?.formatMoney === 'function') {
      return FoxTheme.Currency.formatMoney(amount, moneyFormat);
    }

    if (moneyFormat && typeof Shopify?.formatMoney === 'function') {
      return Shopify.formatMoney(amount, moneyFormat);
    }

    return String(amount);
  }

  /**
   * Reads the live PDP price HTML from the main product block, with Liquid fallback.
   * @returns {string}
   */
  #getPdpFormattedPrice() {
    const fallback = this.dataset.pdpPriceFormatted?.trim() || '';
    const productInfo = document.getElementById(`ProductInfo-${this.#sectionId}`);
    if (!productInfo || !this.#sectionId) return fallback;

    const priceEl = productInfo.querySelector(`#price-${this.#sectionId} .f-price`);
    if (!priceEl) return fallback;

    const isOnSale = priceEl.classList.contains('f-price--on-sale');
    const priceItem = isOnSale
      ? priceEl.querySelector('.f-price__sale .f-price-item--sale')
      : priceEl.querySelector('.f-price__regular .f-price-item--regular');

    return priceItem?.innerHTML?.trim() || priceItem?.textContent?.trim() || fallback;
  }

  /**
   * Writes formatted money HTML into a kit card price node.
   * @param {HTMLElement|null} priceItem - Target price element.
   * @param {string|null|undefined} moneyHtml - Formatted money markup.
   * @returns {void}
   */
  #setPriceItemHtml(priceItem, moneyHtml) {
    if (!priceItem || moneyHtml == null) return;
    priceItem.innerHTML = moneyHtml;
  }

  /**
   * Ensures strikethrough compare markup exists on a kit sale price block.
   * @param {HTMLElement} priceEl - Kit card price wrapper.
   * @param {string|null|undefined} pdpMoney - Formatted PDP compare price HTML.
   * @returns {void}
   */
  #ensureCompareMarkup(priceEl, pdpMoney) {
    const saleBlock = priceEl.querySelector('.f-price__sale');
    if (!saleBlock || pdpMoney == null) return;

    let compareWrap = saleBlock.querySelector('.f-price-item--regular');
    if (!compareWrap) {
      compareWrap = document.createElement('span');
      compareWrap.className = 'f-price-item f-price-item--regular';
      saleBlock.appendChild(compareWrap);
    }

    let compareEl = compareWrap.querySelector('s');
    if (!compareEl) {
      compareEl = document.createElement('s');
      compareWrap.appendChild(compareEl);
    }

    compareEl.innerHTML = pdpMoney;
  }

  /**
   * Builds save-badge HTML from the merchant template and savings amount.
   * Strips content from `<s>` onward to match card-product-horizontal.liquid.
   * @param {number} savingsCents - PDP price minus kit price, in cents.
   * @returns {string}
   */
  #buildSaveLabelHtml(savingsCents) {
    if (savingsCents <= 0) return '';

    const labelTemplate = this.#saveLabelTemplate.split('<s>')[0];

    return labelTemplate.split('[[amount]]').join(this.#formatMoney(savingsCents)).trim();
  }

  /**
   * Returns badge HTML for a kit card (save label or value label).
   * @param {number} pdpPriceCents - Current PDP variant price in cents.
   * @param {number} kitPriceCents - Kit variant price in cents.
   * @returns {string}
   */
  #buildCardBadgeHtml(pdpPriceCents, kitPriceCents) {
    if (!Number.isFinite(kitPriceCents) || kitPriceCents >= pdpPriceCents) {
      return this.#valueLabel;
    }

    const kitSavingsCents = pdpPriceCents - kitPriceCents;
    return this.#buildSaveLabelHtml(kitSavingsCents);
  }

  /**
   * Updates one kit slide badge and price after a PDP variant change.
   * @param {HTMLElement} slide - Kit carousel slide element.
   * @param {number} pdpPriceCents - Current PDP variant price in cents.
   * @param {string} pdpPriceFormatted - Formatted PDP price HTML for compare-at.
   * @returns {void}
   */
  #updateSlideCardPricing(slide, pdpPriceCents, pdpPriceFormatted) {
    const kitPriceCents = Number(slide.dataset.kitPrice);
    if (!Number.isFinite(kitPriceCents)) return;

    const isCheaper = kitPriceCents < pdpPriceCents;
    const badge = slide.querySelector('.product-card__kits-badge');
    const badgesWrap = slide.querySelector('.product-card__kits-badges');
    const priceEl = slide.querySelector('.f-price');

    if (badge) {
      const badgeHtml = this.#buildCardBadgeHtml(pdpPriceCents, kitPriceCents);
      badge.innerHTML = badgeHtml;
      badgesWrap?.classList.toggle('hidden', badgeHtml === '');
    }

    if (!priceEl) return;

    const kitMoney = slide.dataset.kitPriceFormatted;
    const pdpMoney = pdpPriceFormatted;

    if (isCheaper) {
      priceEl.classList.add('f-price--on-sale');
      this.#setPriceItemHtml(priceEl.querySelector('.f-price-item--sale'), kitMoney);
      this.#ensureCompareMarkup(priceEl, pdpMoney);
      return;
    }

    priceEl.classList.remove('f-price--on-sale');
    this.#setPriceItemHtml(priceEl.querySelector('.f-price__regular .f-price-item--regular'), kitMoney);
  }

  /**
   * Re-syncs all kit card badges and compare prices for the active PDP variant.
   * @param {number} pdpPriceCents - Current PDP variant price in cents.
   * @param {string} [pdpPriceFormatted] - Formatted PDP price HTML; defaults to live DOM read.
   * @returns {void}
   */
  #syncAllKitPricing(pdpPriceCents, pdpPriceFormatted = this.#getPdpFormattedPrice()) {
    if (!Number.isFinite(pdpPriceCents) || pdpPriceCents < 1) return;

    this.#currentPdpPriceCents = pdpPriceCents;
    this.dataset.pdpPriceFormatted = pdpPriceFormatted;
    this.#getKitSlides().forEach((slide) => this.#updateSlideCardPricing(slide, pdpPriceCents, pdpPriceFormatted));
  }

  /**
   * Handles PDP variant changes and updates kit savings/compare markup.
   * @param {CustomEvent} event - `variant:changed` event from product-info.
   * @returns {void}
   */
  #onVariantChanged = (event) => {
    const variant = event.detail?.variant;
    if (!variant || !this.#sectionId) return;

    const productInfo = document.getElementById(`ProductInfo-${this.#sectionId}`);
    if (!productInfo?.contains(this)) return;

    this.#syncAllKitPricing(variant.price, this.#getPdpFormattedPrice());
  };

  #onCloseClick = (event) => {
    event.preventDefault();
    this.#dismiss();
  };

  #onMobileTriggerClick = (event, trigger) => {
    event.preventDefault();
    event.stopPropagation();

    if (!this.#isMobileSaveWithKitsLayout()) return;
    if (!this.#panel.classList.contains('is-revealed')) return;
    if (trigger.hasAttribute('hidden')) return;

    // Collapsing goes back to the peek state from page load (heading + top of the cards).
    if (this.#panel.classList.contains('save-with-kits-mobile-expanded')) {
      this.#setMobilePeek(MOBILE_PEEK_ACTIVE);
      return;
    }

    this.#setMobileExpanded(MOBILE_PANEL_EXPANDED);
  };

  #onActionKeypress = (event, target) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    if (target.dataset.action === 'close-kits-popup') {
      this.#onCloseClick(event);
      return;
    }

    if (target.dataset.action === 'toggle-kits-panel') {
      this.#onMobileTriggerClick(event, target);
    }
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.#panel?.classList.contains('is-revealed')) return;

    if (this.#isMobileSaveWithKitsLayout() && this.#panel.classList.contains('save-with-kits-mobile-expanded')) {
      this.#setMobilePeek(MOBILE_PEEK_ACTIVE);
      return;
    }

    this.#dismiss();
  };

  #onResize = () => {
    this.#scheduleSyncSaveWithKitsTop();
    this.#syncMobileBarInset();
    this.#syncSaveWithKitsMobileBodyMaxHeight();
  };

  #onVisualViewportResize = () => {
    this.#scheduleSyncSaveWithKitsTop();
    this.#syncMobileBarInset();
    this.#syncSaveWithKitsMobileBodyMaxHeight();
  };

  #onMobileLayoutMqChange = () => {
    if (!this.#panel) return;

    this.#clearDesktopTopInline();
    this.#panel.classList.remove('save-with-kits-mobile-expanded', 'save-with-kits-mobile-peek');
    this.#panel.querySelector('[data-action="toggle-kits-panel"]')?.setAttribute('aria-expanded', 'false');
    this.#scheduleSyncSaveWithKitsTop();
    this.#syncMobileBarInset();
    this.#syncSaveWithKitsMobileBodyMaxHeight();
  };

  #onLoad = () => {
    this.#scheduleRevealAfterLoad();
  };

  #onPanelTransition = () => {
    if (!this.#isMobileSaveWithKitsLayout()) return;
    this.#syncMobileBarInset();
    requestAnimationFrame(() => this.#syncMobileBarInset());
  };

  #resyncAfterHeader = () => {
    if (this.#isMobileSaveWithKitsLayout()) {
      this.#syncSaveWithKitsMobileBodyMaxHeight();

      if (this.#shouldApplyMobileBarInset()) {
        this.#syncMobileBarInset({
          animate: this.#mobileBarInsetAnimated,
        });
      }

      return;
    }

    this.#syncLayoutAfterHeaderChange();
  };

  #onStickyAtcChange = () => {
    this.#scheduleSyncSaveWithKitsTop();
    this.#syncMobileBarInset();
    requestAnimationFrame(() => this.#syncMobileBarInset());
  };

  #isHelloRetailSearchOpen() {
    if (document.documentElement.classList.contains('hr-search-disable-scroll')) return true;

    const hrResults = document.querySelector(HELLO_RETAIL_RESULTS_SELECTOR);
    if (!hrResults) return false;

    const style = getComputedStyle(hrResults);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
      return false;
    }

    const rect = hrResults.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  #isBehindOverlay() {
    if (OVERLAY_BODY_CLASSES.some((className) => document.body.classList.contains(className))) {
      return true;
    }

    if (this.#isHelloRetailSearchOpen()) return true;

    return Boolean(document.querySelector('.drawer[open]'));
  }

  #syncBehindOverlay = () => {
    if (!this.#panel || this.#userDismissed) return;

    const behind = this.#isBehindOverlay();
    const wasBehind = this.#panel.classList.contains('save-with-kits--behind-overlay');
    this.#panel.classList.toggle('save-with-kits--behind-overlay', behind);

    if (!behind) {
      if (wasBehind && this.#panel.classList.contains('is-revealed')) {
        // The overlay cleared the mobile state; come back in the default peek state.
        if (this.#isMobileSaveWithKitsLayout()) this.#setMobilePeek(MOBILE_PEEK_ACTIVE);
        this.#syncMobileBarInset();
        requestAnimationFrame(() => this.#syncMobileBarInset());
      }
      return;
    }

    this.#panel.classList.remove('save-with-kits-mobile-expanded', 'save-with-kits-mobile-peek');
    this.#panel.querySelector('[data-action="toggle-kits-panel"]')?.setAttribute('aria-expanded', 'false');

    if (this.#isMobileSaveWithKitsLayout()) {
      this.#clearMobileBarInset();
      this.#syncSaveWithKitsMobileBodyMaxHeight();
    }
  };

  #scheduleBehindOverlaySync() {
    if (this.#overlaySyncRaf != null) return;

    this.#overlaySyncRaf = requestAnimationFrame(() => {
      this.#overlaySyncRaf = null;
      this.#syncBehindOverlay();
    });
  }

  #observeOverlayState() {
    if (typeof MutationObserver === 'undefined') {
      this.#syncBehindOverlay();
      return;
    }

    this.#overlayObserver = new MutationObserver(() => this.#scheduleBehindOverlaySync());
    this.#overlayObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });
    this.#overlayObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'open'],
    });
    this.#syncBehindOverlay();
  }

  #isMobileSaveWithKitsLayout() {
    return Boolean(this.#mobileLayoutMq?.matches);
  }

  #clearMobileBarInset() {
    this.#mobileBarInsetAnimated = false;
    document.body.classList.remove(
      'save-with-kits-mobile-bar-enabled',
      'save-with-kits-mobile-bar--animated'
    );
    document.documentElement.style.removeProperty('--save-with-kits-mobile-bar-height');
    document.documentElement.style.removeProperty('--save-with-kits-popup-height');
    document.documentElement.style.removeProperty('--save-with-kits-trigger-height');
    document.documentElement.style.removeProperty('--save-with-kits-sticky-offset');
    document.querySelectorAll('.bb-feedback-button, .bb-feedback-button.gleap-font.gl-block').forEach((el) => {
      el.style.removeProperty('bottom');
    });
  }

  #syncFeedbackButtonBottom(bottomPx, { animate = true } = {}) {
    const bottom = Math.max(0, Math.round(bottomPx || 0));
    document.querySelectorAll('.bb-feedback-button, .bb-feedback-button.gleap-font.gl-block').forEach((el) => {
      el.style.setProperty('bottom', `${bottom}px`, 'important');

      if (animate) {
        el.style.setProperty('transition', 'bottom 0.55s cubic-bezier(0.22, 1, 0.36, 1)', 'important');
      } else {
        el.style.removeProperty('transition');
      }
    });
  }

  #shouldApplyMobileBarInset() {
    if (!this.#isMobileSaveWithKitsLayout() || !this.#panel) return false;

    return (
      this.#panel.classList.contains('is-revealed') ||
      this.#panel.classList.contains('save-with-kits-mobile-peek')
    );
  }

  #syncMobileBarInset({ animate = null } = {}) {
    const docEl = document.documentElement;
    if (!this.#isMobileSaveWithKitsLayout()) {
      this.#clearMobileBarInset();
      this.#syncSaveWithKitsMobileBodyMaxHeight();
      return;
    }

    if (!this.#shouldApplyMobileBarInset()) {
      this.#clearMobileBarInset();
      const stickyAtc = this.#getStickyAtcOffsetPx();
      const mobileSticky = this.#parseCssPx(getComputedStyle(document.documentElement).getPropertyValue('--mobile-sticky-bar-height'));
      const stickyOnlyOffset = stickyAtc > 0
        ? stickyAtc + SAVE_WITH_KITS_FEEDBACK_GAP_PX
        : mobileSticky + SAVE_WITH_KITS_MOBILE_NAV_FALLBACK_OFFSET_PX + SAVE_WITH_KITS_FEEDBACK_GAP_PX;
      this.#syncFeedbackButtonBottom(stickyOnlyOffset, { animate: false });
      this.#syncSaveWithKitsMobileBodyMaxHeight();
      return;
    }

    const shouldAnimate =
      animate ??
      (this.#mobileBarInsetAnimated &&
        this.#panel.classList.contains('is-revealed'));

    document.body.classList.add('save-with-kits-mobile-bar-enabled');
    document.body.classList.toggle(
      'save-with-kits-mobile-bar--animated',
      shouldAnimate
    );
    const height = Math.ceil(this.#panel.getBoundingClientRect().height);
    const triggerBar = this.#panel.querySelector('.product__save-with-kits-bar');
    const triggerHeight = triggerBar ? Math.ceil(triggerBar.getBoundingClientRect().height) : 0;
    const computedStyle = getComputedStyle(docEl);
    const stickyOffset = this.#getStickyAtcOffsetPx();

    docEl.style.setProperty('--save-with-kits-mobile-bar-height', `${height}px`);
    docEl.style.setProperty('--save-with-kits-popup-height', `${height}px`);
    docEl.style.setProperty('--save-with-kits-trigger-height', `${triggerHeight}px`);
    docEl.style.setProperty('--save-with-kits-sticky-offset', `${stickyOffset}px`);
    const feedbackBottom = stickyOffset + height + SAVE_WITH_KITS_FEEDBACK_GAP_PX;
    this.#syncFeedbackButtonBottom(feedbackBottom, { animate: shouldAnimate });
    this.#syncSaveWithKitsMobileBodyMaxHeight();
  }

  #enableMobileBarInsetTransitions() {
    if (this.#mobileBarInsetAnimated) return;

    this.#mobileBarInsetAnimated = true;
    document.body.classList.add('save-with-kits-mobile-bar--animated');
  }

  #primeMobileRevealLayout() {
    if (!this.#isMobileSaveWithKitsLayout() || !this.#panel) return;

    this.#panel.classList.add('save-with-kits-mobile-peek');
    this.#panel.classList.remove('save-with-kits-mobile-expanded');
    this.#updateMobileTriggerA11y();
    this.#syncSaveWithKitsMobileBodyMaxHeight();
    void this.#panel.offsetHeight;
  }

  /**
   * Syncs mobile trigger `aria-expanded` for peek and expanded panel states.
   * @returns {void}
   */
  #updateMobileTriggerA11y() {
    const trigger = this.#panel?.querySelector('[data-action="toggle-kits-panel"]');
    if (!trigger || !this.#isMobileSaveWithKitsLayout()) return;

    const isExpanded = this.#panel.classList.contains('save-with-kits-mobile-expanded');
    const isPeek = this.#panel.classList.contains('save-with-kits-mobile-peek');

    trigger.setAttribute('aria-expanded', isExpanded || isPeek ? 'true' : 'false');
  }

  /**
   * Toggles the mobile peek strip shown on initial reveal.
   * @param {boolean} [active=true] - Whether peek mode is active.
   * @returns {void}
   */
  #setMobilePeek(active = MOBILE_PEEK_ACTIVE) {
    if (!this.#isMobileSaveWithKitsLayout()) return;

    this.#panel.classList.toggle('save-with-kits-mobile-peek', active);
    if (active) {
      this.#panel.classList.remove('save-with-kits-mobile-expanded');
    }

    this.#updateMobileTriggerA11y();
    this.#syncMobileBarInset();
    requestAnimationFrame(() => {
      this.#syncSaveWithKitsMobileBodyMaxHeight();
      this.#syncMobileBarInset();
      this.#updateKitsCarousel();
    });
  }

  #setMobileExpanded(expanded) {
    if (!this.#isMobileSaveWithKitsLayout()) return;

    this.#panel.classList.toggle('save-with-kits-mobile-expanded', expanded);
    this.#panel.classList.remove('save-with-kits-mobile-peek');

    this.#updateMobileTriggerA11y();
    this.#syncMobileBarInset();
    requestAnimationFrame(() => {
      this.#syncSaveWithKitsMobileBodyMaxHeight();
      this.#syncMobileBarInset();
      this.#updateKitsCarousel();
    });
  }

  #updateKitsCarousel() {
    const slider = this.#panel.querySelector('save-with-kits-cards')?.carousel?.slider;
    if (!slider || typeof slider.update !== 'function') return;

    requestAnimationFrame(() => {
      slider.update();
      if (typeof slider.updateSize === 'function') slider.updateSize();
    });
  }

  #dismiss() {
    this.#userDismissed = true;
    this.#panel.classList.remove('is-revealed', 'save-with-kits-mobile-expanded', 'save-with-kits-mobile-peek');
    this.#panel.setAttribute('aria-hidden', 'true');
    this.#panel.querySelector('[data-action="toggle-kits-panel"]')?.setAttribute('aria-expanded', 'false');
    this.#clearMobileBarInset();
    this.#syncSaveWithKitsMobileBodyMaxHeight();
  }

  #parseCssPx(value) {
    const parsed = parseFloat(String(value || '').trim());
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  #getStickyAtcOffsetPx() {
    const stickySelectors = [
      '.sticky-atc-bar.sticky-atc-bar--show',
      'sticky-atc-bar.sticky-atc-bar--show',
      'sticky-atc-bar-minimalist.sticky-atc-bar--show',
    ];
    const visibleBars = [];

    stickySelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => visibleBars.push(el));
    });

    if (!visibleBars.length) return 0;

    const docStyle = getComputedStyle(document.documentElement);
    const isMinimalist = visibleBars.some((el) => el.tagName.toLowerCase() === 'sticky-atc-bar-minimalist');
    const isClassic = visibleBars.some(
      (el) => el.classList.contains('sticky-atc-bar') && el.tagName.toLowerCase() !== 'sticky-atc-bar-minimalist'
    );
    let stickyHeight = this.#parseCssPx(docStyle.getPropertyValue('--sticky-atc-bar-height'));

    if (!stickyHeight) {
      visibleBars.forEach((el) => {
        stickyHeight = Math.max(stickyHeight, el.offsetHeight || el.clientHeight || 0);
      });
    }

    if (isMinimalist && !isClassic) return stickyHeight;

    const mobileSticky = document.body.classList.contains('mobile-sticky-bar-down')
      ? 0
      : this.#parseCssPx(docStyle.getPropertyValue('--mobile-sticky-bar-height'));

    return mobileSticky + stickyHeight;
  }

  #syncSaveWithKitsMobileBodyMaxHeight() {
    const docEl = document.documentElement;

    if (!this.#isMobileSaveWithKitsLayout()) {
      docEl.style.removeProperty('--save-with-kits-mobile-body-max-height');
      return;
    }

    if (!this.#panel) return;

    const bar = this.#panel.querySelector('.product__save-with-kits-bar');
    const barHeight = bar ? Math.ceil(bar.getBoundingClientRect().height) : 0;
    const computedStyle = getComputedStyle(docEl);
    const mobileSticky = this.#parseCssPx(computedStyle.getPropertyValue('--mobile-sticky-bar-height'));
    const stickyAtc = this.#getStickyAtcOffsetPx();
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const available = viewportHeight - mobileSticky - stickyAtc - barHeight - SAVE_WITH_KITS_MOBILE_BODY_NAV_PADDING;

    docEl.style.setProperty('--save-with-kits-mobile-body-max-height', `${Math.max(160, Math.floor(available))}px`);
  }

  #getViewportHeaderTopPx() {
    const top = this.#minTopOf([
      document.querySelector('.shopify-section-group-header-group'),
      document.querySelector('header[is="sticky-header"]'),
    ]);

    return Math.max(0, Number.isFinite(top) ? top : 0);
  }

  #minTopOf(elements) {
    return elements.filter(Boolean).reduce((min, element) => Math.min(min, element.getBoundingClientRect().top), Infinity);
  }

  #elementBottomPx(element) {
    if (!element) return 0;

    const rect = element.getBoundingClientRect();

    if (rect.height >= 1) {
      return Math.ceil(rect.bottom);
    }

    const offsetHeight = element.offsetHeight;

    if (offsetHeight >= 1) {
      return Math.ceil(rect.top + offsetHeight);
    }

    return 0;
  }

  #maxBottomOf(elements) {
    return elements
      .filter(Boolean)
      .reduce((max, element) => Math.max(max, this.#elementBottomPx(element)), 0);
  }

  #getHeaderGroupBottomPx() {
    const group = document.querySelector('.shopify-section-group-header-group');
    if (!group) return 0;

    const groupTop = group.getBoundingClientRect().top;
    const sections = group.querySelectorAll(':scope > .shopify-section');

    if (!sections.length) {
      return this.#elementBottomPx(group);
    }

    const stackedHeight = Array.from(sections).reduce(
      (total, section) => total + (section.offsetHeight || 0),
      0
    );

    if (stackedHeight < 1) {
      return this.#elementBottomPx(group);
    }

    return Math.ceil(groupTop + stackedHeight);
  }

  #getHeaderChromeBottomPx() {
    const group = document.querySelector('.shopify-section-group-header-group');
    const groupSections = group
      ? Array.from(group.querySelectorAll(':scope > .shopify-section'))
      : [];
    const headerChrome = Array.from(
      document.querySelectorAll(
        '.announcement-bar-section, .announcement-bar, announcement-bar, .topbar-section, #theme-header, header[is="sticky-header"], header[is="basic-header"], .header__top, .header__bottom'
      )
    );

    const measuredBottom = this.#maxBottomOf([
      group,
      ...groupSections,
      ...headerChrome,
    ]);
    const groupBottom = this.#getHeaderGroupBottomPx();
    const cssGroupHeight = this.#parseCssPx(
      getComputedStyle(document.documentElement).getPropertyValue(
        '--header-group-height'
      )
    );

    return Math.max(measuredBottom, groupBottom, cssGroupHeight);
  }

  /**
   * Returns the buy-buttons block used as the desktop vertical anchor.
   * @returns {HTMLElement|null}
   */
  #getBuyButtonsAnchor() {
    return this.closest('.product__info-container')?.querySelector('.product__block--buy_buttons') ?? null;
  }

  /**
   * Bottom of the sticky header in the viewport. The announcement bar scrolls away,
   * so the popup follows the header element rather than the full header group.
   * @returns {number}
   */
  #getStickyHeaderBottomPx() {
    const header = document.querySelector('header[is="sticky-header"], header[is="basic-header"]');
    if (!header) return this.#getHeaderChromeBottomPx();

    return Math.max(0, header.getBoundingClientRect().bottom);
  }

  /**
   * Returns the minimum desktop top offset, 20px below the sticky header.
   * @returns {number}
   */
  #getDesktopStickyTopPx() {
    return this.#getStickyHeaderBottomPx() + SAVE_WITH_KITS_DESKTOP_BELOW_CHROME_PX;
  }

  /**
   * Visible top of an element, or null when it is hidden.
   * @param {Element|null|undefined} element
   * @returns {number|null}
   */
  #visibleTopPx(element) {
    if (!element) return null;

    const rect = element.getBoundingClientRect();
    if (rect.height < 1 || getComputedStyle(element).display === 'none') return null;

    return rect.top;
  }

  /**
   * Visible bottom of an element, or null when it is hidden.
   * @param {Element|null|undefined} element
   * @returns {number|null}
   */
  #visibleBottomPx(element) {
    const top = this.#visibleTopPx(element);
    if (top == null) return null;

    return element.getBoundingClientRect().bottom;
  }

  /**
   * Tops the popup must stay 16px clear of: the buy form (covers both the ATC and the
   * notify-me button, which share it) and the visible sticky ATC bar.
   * @returns {number[]}
   */
  #getDesktopCeilingsPx() {
    const panelHeight = this.#panel.offsetHeight;

    return [
      this.#getBuyButtonsAnchor()?.querySelector('.main-product-form'),
      document.querySelector('.sticky-atc-bar--show'),
    ]
      .map((element) => element?.getBoundingClientRect())
      .filter((rect) => rect && rect.height >= 1)
      .map((rect) => Math.round(rect.top - panelHeight - SAVE_WITH_KITS_DESKTOP_ABOVE_ATC_PX));
  }

  /**
   * Desktop popup sits below the title and price, level with the variant picker.
   * Falls back to sitting just above the buy buttons when those anchors are missing.
   * @returns {number|null} Top offset in px, or null when no anchor is available.
   */
  #getDesktopAnchorTopPx() {
    const info = this.closest('.product__info-container');
    const price = info?.querySelector('.product__block--price');
    const variant =
      info?.querySelector('.product__block--variant_picker') ||
      info?.querySelector('.product__block--connected_products');
    const omnibus = price?.nextElementSibling?.classList.contains('product__block--@app')
      ? price.nextElementSibling
      : null;
    const floors = [
      this.#visibleBottomPx(info?.querySelector('.product__block--title')),
      this.#visibleBottomPx(price),
      this.#visibleBottomPx(omnibus),
      this.#visibleTopPx(variant),
    ].filter((value) => value != null);

    if (floors.length) {
      return Math.round(Math.max(...floors));
    }

    const buyButtons = this.#getBuyButtonsAnchor();
    if (!buyButtons || !this.#panel) return null;

    const buyTop = buyButtons.getBoundingClientRect().top;
    const panelHeight = Math.ceil(this.#panel.getBoundingClientRect().height);
    if (!Number.isFinite(buyTop) || panelHeight < 1) return null;

    return Math.round(buyTop - panelHeight - SAVE_WITH_KITS_DESKTOP_ABOVE_BUY_BUTTON_PX);
  }

  /**
   * Clears inline desktop `top` set during scroll anchoring.
   * @returns {void}
   */
  #clearDesktopTopInline() {
    this.#panel?.style.removeProperty('top');
  }

  /**
   * Positions the desktop popup between header chrome and the buy-buttons anchor.
   * @returns {void}
   */
  #syncDesktopTop() {
    if (this.#isMobileSaveWithKitsLayout() || !this.#panel) {
      this.#clearDesktopTopInline();
      return;
    }

    const stickyTop = this.#getDesktopStickyTopPx();
    const anchorTop = this.#getDesktopAnchorTopPx();
    const top = anchorTop == null ? stickyTop : Math.max(stickyTop, anchorTop);

    this.#panel.style.top = `${Math.max(stickyTop, Math.min(top, ...this.#getDesktopCeilingsPx()))}px`;
  }

  #syncSaveWithKitsTop() {
    const docEl = document.documentElement;

    if (this.#isMobileSaveWithKitsLayout()) {
      this.#clearDesktopTopInline();
      docEl.style.removeProperty('--save-with-kits-desktop-top');
      docEl.style.removeProperty('--save-with-kits-header-top');
      docEl.style.removeProperty('--save-with-kits-header-bottom');
      docEl.style.removeProperty('--save-with-kits-popup-margin-top');
      return;
    }

    const headerTop = this.#getViewportHeaderTopPx();
    const chromeBottom = this.#getHeaderChromeBottomPx();
    const top = this.#getDesktopStickyTopPx();

    docEl.style.setProperty('--save-with-kits-desktop-top', `${Math.round(top)}px`);
    docEl.style.setProperty('--save-with-kits-header-top', `${headerTop}px`);
    docEl.style.setProperty('--save-with-kits-header-bottom', `${chromeBottom}px`);
    docEl.style.setProperty('--save-with-kits-popup-margin-top', `${SAVE_WITH_KITS_DESKTOP_BELOW_CHROME_PX}px`);
    this.#syncDesktopTop();
  }

  #scheduleSyncSaveWithKitsTop() {
    if (this.#topSyncPending) return;

    this.#topSyncPending = true;
    this.#topSyncRaf = requestAnimationFrame(() => {
      this.#topSyncRaf = null;
      this.#topSyncPostRaf = requestAnimationFrame(() => {
        this.#topSyncPostRaf = null;
        this.#topSyncPending = false;
        this.#syncSaveWithKitsTop();
      });
    });
  }

  #onScrollLayoutSync = () => {
    if (this.#layoutScrollRaf != null) return;

    this.#layoutScrollRaf = requestAnimationFrame(() => {
      this.#layoutScrollRaf = null;
      this.#scheduleSyncSaveWithKitsTop();
      this.#syncMobileBarInset();
    });
  };

  #prepareRevealPosition() {
    this.#syncSaveWithKitsTop();
    this.#syncSaveWithKitsMobileBodyMaxHeight();

    if (this.#isMobileSaveWithKitsLayout()) {
      this.#primeMobileRevealLayout();
    }
  }

  #finishReveal() {
    if (!this.#abortController || this.#userDismissed || !this.#panel) return;

    if (this.#prefersReducedMotion) {
      this.#panel.classList.add('save-with-kits--no-motion');
    }

    this.#reveal();
  }

  #reveal() {
    if (this.#userDismissed || !this.#panel) return;
    if (this.#panel.classList.contains('is-revealed')) return;

    if (this.#isMobileSaveWithKitsLayout()) {
      this.#prepareRevealPosition();
    } else {
      this.#syncDesktopTop();
    }

    this.#panel.classList.add('is-revealed');
    this.#panel.setAttribute('aria-hidden', 'false');

    if (this.#isMobileSaveWithKitsLayout()) {
      this.#setMobilePeek(MOBILE_PEEK_ACTIVE);
    }

    this.#syncMobileBarInset({ animate: true });
    this.#syncBehindOverlay();
    requestAnimationFrame(() => {
      this.#syncMobileBarInset({ animate: true });
      this.#syncBehindOverlay();

      if (this.#isMobileSaveWithKitsLayout()) {
        this.#syncSaveWithKitsMobileBodyMaxHeight();
        this.#updateKitsCarousel();
        this.#enableMobileBarInsetTransitions();
      }
    });
  }

  #runRevealSequence() {
    if (!this.#abortController || this.#userDismissed) return;
    if (!this.#hydratePanel()) return;

    if (this.#isMobileSaveWithKitsLayout()) {
      this.#prepareRevealPosition();
      window.setTimeout(() => this.#syncMobileBarInset(), 160);
      window.setTimeout(() => this.#syncMobileBarInset(), 420);
    } else {
      this.#syncSaveWithKitsTop();
    }

    this.#finishReveal();
  }

  /**
   * Reveals after window load, then the merchant-configured delay.
   * @returns {void}
   */
  #scheduleRevealAfterLoad() {
    this.#cancelRevealTimeout();

    if (this.#revealDelayMs <= 0) {
      this.#runRevealSequence();
      return;
    }

    this.#revealTimeoutId = window.setTimeout(() => {
      this.#revealTimeoutId = null;
      this.#runRevealSequence();
    }, this.#revealDelayMs);
  }

  #cancelRevealTimeout() {
    if (this.#revealTimeoutId == null) return;

    clearTimeout(this.#revealTimeoutId);
    this.#revealTimeoutId = null;
  }

  #cancelRafs() {
    [this.#layoutScrollRaf, this.#overlaySyncRaf, this.#topSyncRaf, this.#topSyncPostRaf]
      .filter((rafId) => rafId != null)
      .forEach((rafId) => cancelAnimationFrame(rafId));

    this.#layoutScrollRaf = null;
    this.#overlaySyncRaf = null;
    this.#topSyncRaf = null;
    this.#topSyncPostRaf = null;
    this.#topSyncPending = false;
  }

  #clearDocumentStyles() {
    const docEl = document.documentElement;

    this.#panel?.classList.remove('save-with-kits--behind-overlay');
    this.#clearDesktopTopInline();
    this.#clearMobileBarInset();
    docEl.style.removeProperty('--save-with-kits-mobile-body-max-height');
    docEl.style.removeProperty('--save-with-kits-desktop-top');
    docEl.style.removeProperty('--save-with-kits-header-top');
    docEl.style.removeProperty('--save-with-kits-header-bottom');
    docEl.style.removeProperty('--save-with-kits-popup-margin-top');
  }

  #destroy() {
    this.#abortController?.abort();
    this.#delegate?.destroy();

    if (this.#mobileLayoutMq?.removeEventListener) {
      this.#mobileLayoutMq.removeEventListener('change', this.#onMobileLayoutMqChange);
    } else if (this.#mobileLayoutMq?.removeListener) {
      this.#mobileLayoutMq.removeListener(this.#onMobileLayoutMqChange);
    }

    this.#kitsPanelObserver?.disconnect();
    this.#headerObserver?.disconnect();
    this.#headerMutationObserver?.disconnect();
    this.#buyButtonsObserver?.disconnect();
    this.#overlayObserver?.disconnect();
    this.#cancelRafs();
    this.#cancelRevealTimeout();
    this.#clearDocumentStyles();
    this.#restorePanelMount();

    this.#abortController = null;
    this.#delegate = null;
    this.#headerObserver = null;
    this.#headerMutationObserver = null;
    this.#buyButtonsObserver = null;
    this.#kitsPanelObserver = null;
    this.#overlayObserver = null;
    this.#mobileLayoutMq = null;
    this.#panel = null;
    this.#saveLabelTemplate = '';
    this.#valueLabel = '';
    this.#sectionId = '';
    this.#currentPdpPriceCents = 0;
    this.#revealDelayMs = 0;
  }
}

customElements.define('product-save-with-kits', ProductSaveWithKits);
